import React, { useState, useEffect, useMemo } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { PettyCash, PettyCashExpense, Employee } from '../../types';
import { useEmployees } from '../../hooks/useEmployees';
import { useBranches } from '../../hooks/useBranches';
import { useCategories } from '../../hooks/useCategories';
import { usePettyCash } from '../../hooks/usePettyCash';
import { useTransactions } from '../../hooks/useTransactions';
import Select from './Select';
import Input from './Input';
import { formatDateForAPI, formatDateForInput } from '../../utils/dateFormatter';
import { PettyCashExpensesTable, PettyCashFinancialSummary, PettyCashFormActions } from './PettyCashSections';
import {
  employeesQO, branchesQO, buildBranchNameOptions, buildClassOptions,
  buildExpenseBranchOptions, applyEmployeeBalance,
} from './PettyCashSections/pettyCashFormHelpers';

interface PettyCashFormProps {
  pettyCash?: PettyCash;
  onSubmit: (data: Partial<PettyCash>) => void;
  onCancel: () => void;
  onApprove?: (record: PettyCash) => void;
  onReject?: (record: PettyCash) => void;
}

const recordsQO = {
  sort: [{ field: 'date', order: 'desc' as const }],
  pagination: { pageSize: 500 },
  populate: '*', // employee relation needed for the previous-balance lookup
};

const outTxQO = {
  filters: { type: { $eq: 'out' } },
  sort: [{ field: 'date', order: 'desc' as const }],
  pagination: { pageSize: 500 },
};

const PettyCashForm: React.FC<PettyCashFormProps> = ({ pettyCash, onSubmit, onCancel, onApprove, onReject }) => {
  const { data: apiEmployees = [] } = useEmployees(employeesQO);
  const { data: branches = [] } = useBranches(branchesQO);
  const { data: apiCategories = [] } = useCategories(branchesQO);
  const { data: pettyRecords = [] } = usePettyCash(recordsQO);
  const { data: outTransactions = [] } = useTransactions(outTxQO);
  
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    watch,
    setValue
  } = useForm<PettyCash>({
    defaultValues: pettyCash ? {
      ...pettyCash,
      date: formatDateForInput(pettyCash.date)
    } : {
      employeeName: '',
      employeeId: '',
      date: new Date().toISOString().split('T')[0],
      location: '',
      status: 'pending',
      previousBalance: 0,
      totalReceived: 0,
      totalExpenses: 0,
      finalBalance: 0,
      expenses: []
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'expenses'
  });

  const watchedExpenses = watch('expenses');
  const watchedPreviousBalance = watch('previousBalance');
  const watchedTotalReceived = watch('totalReceived');

  const employeeOptions = useMemo(() => {
    if (!apiEmployees || apiEmployees.length === 0) return [];
    return apiEmployees.map((emp: any) => ({
      value: emp.documentId || emp.id?.toString() || '',
      label: `${emp.name || 'Unknown'} (${emp.code || emp.id || 'N/A'})`
    })).filter((opt: any) => opt.value);
  }, [apiEmployees]);

  // Locations come from the branches API: both headquarters and vessels
  const locationOptions = useMemo(
    () => buildBranchNameOptions(branches as any[], pettyCash?.location),
    [branches, pettyCash?.location]
  );
  const classOptions = useMemo(
    () => buildClassOptions(apiCategories as any[], pettyCash?.expenses),
    [apiCategories, pettyCash?.expenses]
  );
  const branchOptions = useMemo(
    () => buildExpenseBranchOptions(branches as any[], pettyCash?.expenses),
    [branches, pettyCash?.expenses]
  );

  // Balance is cumulative per employee: a new record starts from the last
  // final balance, and out-transactions paid to the employee since that
  // statement auto-fill "Total Received" (backend-computed when available)
  const watchedEmployeeId = watch('employeeId');
  useEffect(() => {
    if (pettyCash || !watchedEmployeeId) return;
    applyEmployeeBalance({
      employeeId: watchedEmployeeId,
      records: pettyRecords as PettyCash[],
      transactions: outTransactions as any[],
      employees: apiEmployees,
      setValue: (field, value) => setValue(field, value),
    });
  }, [pettyCash, watchedEmployeeId, pettyRecords, outTransactions, apiEmployees, setValue]);

  const handleEmployeeChange = (employeeId: string) => {
    const employee = apiEmployees.find((emp: any) => 
      emp.documentId === employeeId || emp.id?.toString() === employeeId
    );
    if (employee) {
      setSelectedEmployee(employee as Employee);
      setValue('employeeId', employee.documentId || employee.id?.toString() || '');
      setValue('employeeName', employee.name || '');
    }
  };

  useEffect(() => {
    // Inputs yield strings; force numeric math so 1000 + 500 = 1500, not "1000500"
    const totalExpenses = (watchedExpenses || []).reduce(
      (sum, expense) => sum + (Number(expense.amount) || 0), 0
    );
    const finalBalance = (Number(watchedPreviousBalance) || 0) + (Number(watchedTotalReceived) || 0) - totalExpenses;

    setValue('totalExpenses', totalExpenses);
    setValue('finalBalance', finalBalance);
  }, [watchedExpenses, watchedPreviousBalance, watchedTotalReceived, setValue]);

  const addExpense = () => {
    const newExpense: PettyCashExpense = {
      id: Date.now().toString(),
      number: fields.length + 1,
      description: '',
      class: '',
      category: '',
      amount: 0
    };
    append(newExpense);
  };

  const handleFormSubmit = (data: PettyCash) => {
    // employeeName is only set by the dropdown's change event; derive it from
    // the selected id so it is never submitted empty
    const emp: any = apiEmployees.find((e: any) =>
      e.documentId === data.employeeId || String(e.id) === String(data.employeeId)
    );
    const submissionData = {
      ...data,
      employeeName: emp?.name || data.employeeName || '',
      // Amounts arrive as strings from the inputs; the backend expects numbers
      expenses: (data.expenses || []).map((e) => ({ ...e, amount: Number(e.amount) || 0 })),
      previousBalance: Number(data.previousBalance) || 0,
      totalReceived: Number(data.totalReceived) || 0,
      totalExpenses: Number(data.totalExpenses) || 0,
      finalBalance: Number(data.finalBalance) || 0,
      date: formatDateForAPI(data.date),
      id: pettyCash?.id || Date.now().toString(),
      createdAt: pettyCash?.createdAt || new Date().toISOString()
    };
    onSubmit(submissionData);
  };

  return (
    <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-lg">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {pettyCash ? 'Edit Petty Cash Record' : 'New Petty Cash Record'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              كشف العهدة المالية اليومي - Petty Cash Daily Statement
            </p>
          </div>
          {pettyCash && pettyCash.status && (
            <div>
              {pettyCash.status === 'pending' && (
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                  ⏳ Pending Approval
                </span>
              )}
              {pettyCash.status === 'approved' && (
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-300">
                  ✓ Approved
                </span>
              )}
              {pettyCash.status === 'rejected' && (
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-red-100 text-red-800 border border-red-300">
                  ✕ Rejected
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6 space-y-6">
        {/* Basic Information */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900">Basic Information - المعلومات الأساسية</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <Select
                  label="Employee - الموظف"
                  options={employeeOptions}
                  placeholder="Select Employee - اختر الموظف"
                  required
                  {...register('employeeId', { 
                    required: 'Employee is required - يجب اختيار الموظف',
                    onChange: (e) => handleEmployeeChange(e.target.value)
                  })}
                  error={errors.employeeId?.message}
                />
              </div>
              <div>
                <Input
                  label="Date - التاريخ"
                  type="date"
                  {...register('date', { required: 'Date is required' })}
                  error={errors.date?.message}
                />
              </div>
              <div>
                <Select
                  label="Location - الموقع"
                  options={locationOptions}
                  placeholder="Select Location - اختر الموقع"
                  required
                  {...register('location', { required: 'Location is required' })}
                  error={errors.location?.message}
                />
              </div>
              <div>
                <Select
                  label="Status - الحالة"
                  options={[
                    { value: 'pending', label: 'Pending - قيد الانتظار' },
                    { value: 'approved', label: 'Approved - موافق عليه' },
                    { value: 'rejected', label: 'Rejected - مرفوض' }
                  ]}
                  {...register('status', { required: 'Status is required' })}
                  error={errors.status?.message}
                />
              </div>
            </div>
            {watch('status') === 'rejected' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason - سبب الرفض
                </label>
                <textarea
                  {...register('rejectedReason')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter the reason for rejection / أدخل سبب الرفض"
                />
              </div>
            )}
          </div>
        </div>

        <PettyCashFinancialSummary register={register} errors={errors} />

        <PettyCashExpensesTable
          fields={fields}
          register={register}
          remove={remove}
          addExpense={addExpense}
          classOptions={classOptions}
          categoryOptions={branchOptions}
        />

        <PettyCashFormActions
          pettyCash={pettyCash}
          isSubmitting={isSubmitting}
          onCancel={onCancel}
          onApprove={onApprove}
          onReject={onReject}
        />
      </form>
    </div>
  );
};

export default PettyCashForm;
